/**
 * Line Tool Shell
 *
 * Coordinates between automatic and manual line detection modes.
 * Owns shared infrastructure (plane preview, visualizer, Ctrl-refine)
 * and intercepts events for shared behavior before delegating to modes.
 */

import { SubToolBase } from '../../SubToolBase';
import { ISubTool } from '../../SubTool';
import { Viewer } from '../../../viewer/viewer';
import { IPersona, IViewConfig, ToolEvents } from '../../types';
import { ToolSettingsSchema, ToolSettingsChangeEvent } from '../../ToolSettings';
import { LineDetectionConfig, LinePrompt } from '../modules/line-detection/types';
import { AutomaticLineMode } from './line-tool/AutomaticLineMode';
import { ManualLineMode } from './line-tool/ManualLineMode';
import { LineToolMode, LineToolContext, PromptSnapshot } from './line-tool/types';
import { Snapshot } from '../ToolUndoRedoManager';
import { PointoramaPlane } from '../../../utils/PointoramaPlane';
import { PlaneCreator } from '../modules/PlaneCreator';
import { LinePromptVisualizer } from '../modules/line-detection/LinePromptVisualizer';
import { CoordinateTransformer } from '../modules/utils/CoordinateTransformer';
import { LineSelectionHitTester } from '../modules/line-detection/LineSelectionHitTester';
import { ClipTask } from '../../../defines';
import { AnnotationKindAdapter } from './line-tool/AnnotationKindAdapter';
import { RoomKindAdapter } from './line-tool/RoomKindAdapter';
import { BasePlaneState } from './line-tool/SelectionContextProvider';
import * as THREE from 'three';
import { t } from '../../../translation/markers';

class LineToolSnapshot implements Snapshot {
  constructor(
    private readonly tool: LineTool,
    private data: PromptSnapshot,
  ) { }

  restore(): Snapshot {
    const mode = this.tool.getActiveMode();
    const currentData = mode.captureUndoSnapshot();
    mode.prepareForUndoRedo();
    mode.restoreFromSnapshot(this.data);
    this.data = currentData;
    return this;
  }
}

export class LineTool extends SubToolBase implements LineToolContext, ISubTool {
  readonly name = 'line_detection';

  // Mode instances
  private automaticMode: AutomaticLineMode;
  private manualMode: ManualLineMode;

  // Current mode
  private currentMode: LineToolMode = LineToolMode.AUTOMATIC;

  // Settings
  private settings = {
    mode: LineToolMode.AUTOMATIC,
    temporaryPointSize: 1.0,
    enableCustomSegmentIsolation: false,
    segmentIsolationTolerance: 0.1,
    segmentIsolationHeight: 1.2,
  };

  // ========== Shared infrastructure (LineToolContext) ==========

  readonly planePreviewScene: THREE.Scene;
  readonly _activePlane: PointoramaPlane;
  readonly planeCreator: PlaneCreator;
  readonly visualizer: LinePromptVisualizer;
  readonly coordinateTransformer: CoordinateTransformer;
  readonly hitTester: LineSelectionHitTester;
  readonly mouse = new THREE.Vector2();
  readonly planeClickDataByPromptId = new Map<string, { center: THREE.Vector3; grid: THREE.Vector3[] }>();

  // Unregister handle for the plane preview overlay scene
  private unregisterPlanePreviewOverlay: (() => void) | null = null;

  // Ctrl-refine state
  private refiningPrompt: LinePrompt | null = null;
  private refinementClickCount = 0;
  private originalRefineDirection: THREE.Vector3 | null = null;

  // Per-kind plug-in (rooms by default; door/window registers a separate
  // LineTool with its own adapter). Owned and exposed via LineToolContext.
  readonly kindAdapter: AnnotationKindAdapter;

  // Current base plane, pushed down by the host via setBasePlane. The host
  // owns the selection context; the subtool only consumes the resulting plane.
  private currentBasePlane: BasePlaneState | null = null;

  // Drag state forwarded by the host (the host owns DOM input + drag detection).
  private hostDragging = false;

  // The host sets this to be notified when a custom-segment-isolation setting
  // changes, so it can re-push isolation (the host owns the selection context)
  // and re-feed the base plane. No-op when unhosted.
  private onIsolationSettingsChanged: (() => void) | null = null;

  constructor(viewer: Viewer, persona: IPersona, config?: LineDetectionConfig, kindAdapter?: AnnotationKindAdapter) {
    super(viewer, persona);

    this.kindAdapter = kindAdapter ?? new RoomKindAdapter();

    // Initialize undo/redo for this subtool (shared across modes)
    this.initUndoRedo();

    // Create shared infrastructure
    this.coordinateTransformer = new CoordinateTransformer(viewer);

    this.planePreviewScene = new THREE.Scene();
    this.planePreviewScene.name = 'line_tool_plane_preview_scene';
    this.viewer.inputHandler.registerInteractiveScene(this.planePreviewScene);

    this.hitTester = new LineSelectionHitTester(this.coordinateTransformer);
    this.visualizer = new LinePromptVisualizer(this.viewer, this.coordinateTransformer, this.planePreviewScene);

    this._activePlane = this.createActivePlane();
    this.planeCreator = new PlaneCreator(this.viewer, this._activePlane);
    this.planePreviewScene.add(this.planeCreator);

    // Initialize both modes, passing this tool as context
    this.automaticMode = new AutomaticLineMode(viewer, persona, config, this);
    this.manualMode = new ManualLineMode(viewer, persona, config, this);
  }

  /**
   * Called by modes (AutomaticLineMode, ManualLineMode) to propagate
   * state changes to the quest/tooltip system.
   */
  dispatchToolPhase(phase: string): void {
    this.dispatchStateChanged({ phase });
  }

  /**
   * Push a temporary hint override that takes priority over the phase-based hint.
   * Call popHintOverride() to revert to the current phase hint.
   */
  pushHintOverride(hintSourceString: string): void {
    this.dispatchEvent({ type: ToolEvents.HINT_OVERRIDE, tool: this, override: hintSourceString });
    this.viewer.scene.dispatchEvent({ type: ToolEvents.HINT_OVERRIDE, tool: this, override: hintSourceString });
  }

  /**
   * Pop the current hint override, reverting to the phase-based hint.
   */
  popHintOverride(): void {
    this.dispatchEvent({ type: ToolEvents.HINT_OVERRIDE, tool: this, override: null });
    this.viewer.scene.dispatchEvent({ type: ToolEvents.HINT_OVERRIDE, tool: this, override: null });
  }

  isCameraDragging(): boolean {
    return this.hostDragging;
  }

  /** The host forwards its drag-detection state each move. */
  setHostDragging(dragging: boolean): void {
    this.hostDragging = dragging;
  }

  /**
   * The host registers this to re-push floor isolation when a custom-segment
   * isolation setting changes (the host owns the selection context now).
   */
  setIsolationSettingsChangedHandler(handler: (() => void) | null): void {
    this.onIsolationSettingsChanged = handler;
  }

  /** Live custom-segment-isolation settings, read by a floor host's context. */
  getCustomIsolationSettings(): { enableCustomSegmentIsolation: boolean; segmentIsolationHeight: number; segmentIsolationTolerance: number } {
    return {
      enableCustomSegmentIsolation: this.settings.enableCustomSegmentIsolation,
      segmentIsolationHeight: this.settings.segmentIsolationHeight,
      segmentIsolationTolerance: this.settings.segmentIsolationTolerance,
    };
  }

  // ========== Undo/Redo ==========

  /**
   * Capture current state as a snapshot and push it onto the undo stack.
   * Called by modes before mutations.
   */
  pushUndoSnapshot(): void {
    this.undoRedo?.pushState(new LineToolSnapshot(this, this.getActiveMode().captureUndoSnapshot()));
  }

  /**
   * Get the currently active mode instance
   */
  getActiveMode(): AutomaticLineMode | ManualLineMode {
    return this.currentMode === LineToolMode.AUTOMATIC ? this.automaticMode : this.manualMode;
  }

  get activePlane(): PointoramaPlane {
    return this._activePlane;
  }

  // ========== Plane Preview ==========

  private createActivePlane(): PointoramaPlane {
    const newPlane = new PointoramaPlane({
      clipTask: ClipTask.NONE,
      tolerance: 0.01,
    });
    newPlane.visible = false;
    this.planePreviewScene.add(newPlane);
    return newPlane;
  }

  private updatePlanePreview(): void {
    const camera = this.viewer.scene.getActiveCamera();
    const is3DMode = camera.type === 'PerspectiveCamera';

    const basePlane = this.currentBasePlane;
    if (is3DMode && basePlane) {
      if (this.isCameraDragging()) {
        this._activePlane.visible = false;
        this._activePlane.unHighlight();
        this.visualizer.clearHoverPreview();
        return;
      }

      this.planeCreator.adjustActivePlaneToMousePosition(this.mouse);

      if (this._activePlane.visible) {
        const worldPoint = this._activePlane.position.clone();
        worldPoint.z = basePlane.origin.z;
        const direction = this.getDirectionFromPlaneNormal();
        this.visualizer.renderHoverPreview(worldPoint, direction);
      } else {
        this.visualizer.clearHoverPreview();
      }
    } else {
      this._activePlane.visible = false;
      this._activePlane.unHighlight();
      this.visualizer.clearHoverPreview();
    }
  }

  getDirectionFromPlaneNormal(): THREE.Vector3 {
    const planeNormal = this._activePlane.getNormal();
    if (Math.abs(planeNormal.z) > 0.9) {
      return new THREE.Vector3(1, 0, 0);
    }
    return new THREE.Vector3(-planeNormal.y, planeNormal.x, 0).normalize();
  }

  // ========== 3D Plane Click ==========

  private handlePlaneClick(): void {
    const basePlane = this.currentBasePlane;
    if (!basePlane || !this._activePlane.visible) return;

    this.pushUndoSnapshot();

    const worldPoint = this._activePlane.position.clone();
    worldPoint.z = basePlane.origin.z;
    const direction = this.getDirectionFromPlaneNormal();

    const mode = this.getActiveMode();
    const promptId = mode.getNextPromptId();

    const clickData = this.planeCreator.getPointsAroundMouse(this.mouse);
    if (clickData) {
      this.planeClickDataByPromptId.set(promptId, clickData);
    }

    const prompt: LinePrompt = {
      id: promptId,
      worldPoint,
      direction,
      screenPosition: new THREE.Vector2(),
    };

    mode.addPrompt(prompt);
    mode.updateShapePreview();
  }

  // ========== Ctrl-Refine ==========

  private startPlaneRefinement(): void {
    const mode = this.getActiveMode();
    if (mode.getLinePrompts().length === 0) return;
    if (mode.isPromptSphereDragging()) return;

    const camera = this.viewer.scene.getActiveCamera();
    if (camera.type !== 'PerspectiveCamera') return;

    const prompts = mode.getLinePrompts();
    const lastPrompt = prompts[prompts.length - 1];
    this.refiningPrompt = lastPrompt;
    this.refinementClickCount = 0;
    this.originalRefineDirection = lastPrompt.direction.clone();

    this.planeCreator.reset();

    const clickData = this.planeClickDataByPromptId.get(lastPrompt.id);
    if (clickData) {
      this.planeCreator.selectedFittingPoints.push({
        center: clickData.center.clone(),
        grid: clickData.grid.map((p: THREE.Vector3) => p.clone()),
      });
      this.planeCreator.addMarkerToScene(clickData.center);
      this.planeCreator.position.copy(clickData.center);
      this._activePlane.position.copy(clickData.center);
    }

    this.pushHintOverride('ctrl_refine');
  }

  private finalizePlaneRefinement(): void {
    if (!this.refiningPrompt) return;

    if (this.refinementClickCount > 0) {
      this.planeCreator.adjustActivePlane();

      const refinedDirection = this.getDirectionFromPlaneNormal();
      const originalDir = this.originalRefineDirection;
      if (!originalDir) return;

      if (refinedDirection.dot(originalDir) < 0) {
        refinedDirection.negate();
      }

      const blendFactor = this.getRefinementBlendFactor(this.refinementClickCount);
      const blendedDirection = originalDir.clone().lerp(refinedDirection, blendFactor).normalize();

      this.pushUndoSnapshot();
      this.refiningPrompt.direction.copy(blendedDirection);
      this.getActiveMode().updateShapePreview();
    }

    this.refiningPrompt = null;
    this.originalRefineDirection = null;
    this.refinementClickCount = 0;
    this.cleanupRefinementVisuals();
    this.popHintOverride();
  }

  cancelPlaneRefinement(): void {
    if (!this.refiningPrompt) return;
    this.refiningPrompt = null;
    this.originalRefineDirection = null;
    this.refinementClickCount = 0;
    this.cleanupRefinementVisuals();
    this.popHintOverride();
  }

  private cleanupRefinementVisuals(): void {
    this.planeCreator.reset();
    this._activePlane.visible = false;
    this.planeCreator.angleLabel.visible = false;
  }

  private handleRefinementClick(): void {
    const result = this.planeCreator.addMarker(this.mouse);
    if (result === 'added') {
      this.refinementClickCount++;
    } else if (result === 'finish') {
      this.finalizePlaneRefinement();
    }
  }

  private getRefinementBlendFactor(clickCount: number): number {
    if (clickCount <= 1) return 0.5;
    if (clickCount === 2) return 0.75;
    return 0.9;
  }

  get isRefining(): boolean {
    return this.refiningPrompt !== null;
  }

  /**
   * Switch to a different mode
   */
  private switchMode(newMode: LineToolMode): void {
    if (newMode === this.currentMode) return;

    // Get the current mode and save its state
    const currentActiveMode = this.getActiveMode();
    const linePrompts = currentActiveMode.getLinePrompts();
    const editingShape = currentActiveMode.getEditingShape();

    // Clear shape edit reference before deactivating so deactivate() doesn't cancel it
    if (editingShape) {
      currentActiveMode.setEditingShape(null);
    }

    // Deactivate current mode
    currentActiveMode.deactivate();

    // Switch mode
    this.currentMode = newMode;

    // Activate new mode and pass current base-plane state
    const newActiveMode = this.getActiveMode();
    const basePlane = this.currentBasePlane;
    if (basePlane !== null) {
      newActiveMode.onBasePlaneChanged(basePlane);
    }

    // Transfer line prompts to the new mode before activating
    if (linePrompts.length > 0) {
      newActiveMode.setLinePrompts(linePrompts);
    }

    // Transfer shape edit state to the new mode
    if (editingShape) {
      newActiveMode.setEditingShape(editingShape);
    }

    newActiveMode.activate();
  }

  // ========== Lifecycle (subtool, host-driven) ==========

  protected onActivate(): void {
    this.viewer.orbitControls.doubleClickZoomEnabled = false;

    // Register the plane preview scene as a tool overlay so the pipeline
    // draws it with each view's own camera (main + widgets).
    this.unregisterPlanePreviewOverlay = this.viewer.registerToolOverlayScene(this.planePreviewScene);

    // The host pushes the current base plane via setBasePlane (on activate and
    // on selection change), so the active mode already has it by now.
    this.getActiveMode().activate();
  }

  protected onDeactivate(): void {
    this.getActiveMode().deactivate();
    this.viewer.orbitControls.doubleClickZoomEnabled = true;

    this.unregisterPlanePreviewOverlay?.();
    this.unregisterPlanePreviewOverlay = null;

    // Clean up plane preview state
    this._activePlane.visible = false;
    this._activePlane.unHighlight();
    this.visualizer.clearHoverPreview();
    this.cancelPlaneRefinement();
  }

  /**
   * The host pushes the current base plane here (on activate + selection
   * change + isolation settings change). Feeds the active mode.
   */
  setBasePlane(state: BasePlaneState | null): void {
    this.currentBasePlane = state;
    this.getActiveMode().onBasePlaneChanged(state);
  }

  // ========== Input Events (intercept shared behavior, then delegate) ==========

  onMouseDown(event: MouseEvent): void {
    const mode = this.getActiveMode();
    if (mode.onMouseDown) mode.onMouseDown(event);
  }

  onMouseMove(event: MouseEvent): void {
    // Shared: update mouse position for plane preview
    const rect = this.viewer.renderer.domElement.getBoundingClientRect();
    this.mouse.x = event.clientX - rect.left;
    this.mouse.y = event.clientY - rect.top;

    // Shared: update plane preview in 3D mode
    this.updatePlanePreview();

    // Skip mode delegation while dragging the camera in 3D — no hover effects during orbit
    const camera = this.viewer.scene.getActiveCamera();
    if (camera.type === 'PerspectiveCamera' && this.isCameraDragging()) return;

    // Delegate mode-specific handling
    const mode = this.getActiveMode();
    if (mode.onMouseMove) mode.onMouseMove(event);
  }

  onMouseUp(event: MouseEvent): void {
    const mode = this.getActiveMode();
    if (mode.onMouseUp) mode.onMouseUp(event);
  }

  /**
   * Called by modes from their 3D non-drag click path.
   * Routes to shape selection (via kind adapter), refinement click, or plane click.
   */
  handle3DClick(): void {
    if (this.refiningPrompt) {
      this.handleRefinementClick();
      return;
    }

    // Try kind-specific shape selection before plane click (idle only).
    const mode = this.getActiveMode();
    if (!mode.isEditingShape() && mode.getLinePrompts().length === 0) {
      const ctx = {
        viewer: this.viewer,
        persona: this.persona,
        mouse: this.mouse,
        coordinateTransformer: this.coordinateTransformer,
        basePlane: this.currentBasePlane,
      };
      const clickedShape = this.kindAdapter.hitTestEditableTarget(ctx);
      if (clickedShape) {
        this.kindAdapter.configureViewForShape(clickedShape, this.viewer);
        mode.enterShapeEditMode(clickedShape);
        return;
      }
    }

    this.handlePlaneClick();
  }

  onClick(event: MouseEvent): void {
    // In 3D mode, BaseTool guarantees onClick only fires for non-drag clicks
    const camera = this.viewer.scene.getActiveCamera();
    if (camera.type === 'PerspectiveCamera') {
      this.handle3DClick();
      return;
    }

    const mode = this.getActiveMode();
    if (mode.onClick) mode.onClick(event);
  }

  onRightClick(event: MouseEvent): void {
    // Intercept: during Ctrl-refine, right-click removes last marker
    if (this.refiningPrompt) {
      if (this.refinementClickCount > 0) {
        this.planeCreator.removeMarker();
        this.refinementClickCount--;
      }
      return;
    }

    const mode = this.getActiveMode();
    if (mode.onRightClick) mode.onRightClick(event);
  }

  onKeyDown(event: KeyboardEvent): void {
    const mode = this.getActiveMode();

    // Intercept Escape during refinement
    if (event.key === 'Escape' && this.refiningPrompt) {
      this.cancelPlaneRefinement();
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // Intercept Ctrl for refinement start (3D + prompts + not dragging)
    if (event.key === 'Control' && !event.repeat) {
      const camera = this.viewer.scene.getActiveCamera();
      const is3D = camera.type === 'PerspectiveCamera';
      if (is3D && mode.getLinePrompts().length > 0 && !mode.isPromptSphereDragging()) {
        this.startPlaneRefinement();
        // Still delegate so mode can update its ctrlDown state
        if (mode.onKeyDown) mode.onKeyDown(event);
        return;
      }
    }

    if (mode.onKeyDown) mode.onKeyDown(event);
  }

  onKeyUp(event: KeyboardEvent): void {
    const mode = this.getActiveMode();

    // Intercept Ctrl release to finalize refinement
    if (event.key === 'Control' && this.refiningPrompt) {
      this.finalizePlaneRefinement();
      // Still delegate so mode can update its ctrlDown state
      if (mode.onKeyUp) mode.onKeyUp(event);
      return;
    }

    if (mode.onKeyUp) mode.onKeyUp(event);
  }

  onWheel(event: WheelEvent): void {
    const mode = this.getActiveMode();
    if (mode.onWheel) mode.onWheel(event);
  }

  // ========== View & Settings ==========

  getViewConfig(): IViewConfig {
    return this.kindAdapter.getViewConfig();
  }

  getSettingsSchema(): ToolSettingsSchema {
    return {
      mode: {
        type: 'select',
        label: t('Mode'),
        description: 'Line detection mode',
        defaultValue: this.settings.mode,
        options: [
          { label: t('Magic'), value: LineToolMode.AUTOMATIC },
          { label: t('Manual'), value: LineToolMode.MANUAL },
        ],
      },
      temporaryPointSize: {
        type: 'slider',
        label: t('Point size'),
        description: 'Size of points',
        defaultValue: this.settings.temporaryPointSize,
        min: 0.1,
        max: 3.0,
        step: 0.1,
        unit: 'px',
      },
      enableCustomSegmentIsolation: {
        type: 'category',
        label: t('Custom view'),
        description: 'Use custom view settings',
        defaultValue: this.settings.enableCustomSegmentIsolation,
      },
      segmentIsolationHeight: {
        type: 'slider',
        label: t('Floor elevation'),
        description: 'Height above floor',
        defaultValue: this.settings.segmentIsolationHeight,
        min: 1.0,
        max: 10.0,
        step: 0.1,
        unit: 'm',
        decimals: 2,
        group: 'enableCustomSegmentIsolation',
      },
      segmentIsolationTolerance: {
        type: 'slider',
        label: t('Floor height'),
        description: 'Thickness of visible slice',
        defaultValue: this.settings.segmentIsolationTolerance,
        min: 0.001,
        max: 5.0,
        step: 0.001,
        unit: 'm',
        decimals: 2,
        group: 'enableCustomSegmentIsolation',
      },
    };
  }

  onSettingsChange(event: ToolSettingsChangeEvent): void {
    const { key, value } = event;

    // Handle mode switching
    if (key === 'mode') {
      this.settings.mode = value as LineToolMode;
      this.switchMode(value as LineToolMode);
      return;
    }

    // Update local settings
    if (key === 'temporaryPointSize') {
      this.settings.temporaryPointSize = value as number;
    } else if (key === 'enableCustomSegmentIsolation') {
      this.settings.enableCustomSegmentIsolation = value as boolean;
    } else if (key === 'segmentIsolationTolerance') {
      this.settings.segmentIsolationTolerance = value as number;
    } else if (key === 'segmentIsolationHeight') {
      this.settings.segmentIsolationHeight = value as number;
    }

    // Forward settings changes to active mode
    this.getActiveMode().onSettingsChange(event);

    // Re-apply isolation if settings that affect the segment slice changed.
    // The host owns the selection context + strategy, so notify it; it re-pushes
    // isolation and re-feeds our base plane via setBasePlane.
    if (key === 'enableCustomSegmentIsolation' || key === 'segmentIsolationHeight' || key === 'segmentIsolationTolerance') {
      this.onIsolationSettingsChanged?.();
    }
  }

  // ========== Cleanup ==========

  dispose(): void {
    this.automaticMode.dispose();
    this.manualMode.dispose();
  }
}
